import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { queryKeys } from '@/lib/queryKeys';
import { track } from '@/modules/Analytics';
import {
  Project,
  Task,
  TaskCostPrice,
  TaskEconomyReport,
  TaskExpense,
  TaskHourEstimate,
  TaskSalesPrice,
} from '@/types/types';

// Fetch all project assignments
export const useTasks = () => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.tasks.all.queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<Task[]>(`/api/v1/tasks`);
      return data;
    },
  });
};
// Fetch all project assignments
export const useTasksByProject = (projectId?: Project['id']) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.tasks.byProject(projectId).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<Task[]>(`/api/v1/tasks`, {
        params: projectId ? { project_id: projectId } : {},
      });
      return data;
    },
    enabled: !!projectId,
  });
};

type PagyMeta = {
  count: number;
  page: number;
  pages: number;
  items: number;
  next?: number;
  prev?: number;
};

export type TaskGroupCount = { id: number | null; count: number };

export type ServerGroupBy = 'category' | 'location' | 'milestone' | 'trade';

export type TasksResponse = {
  tasks: Task[];
  pagy: PagyMeta;
  groups?: TaskGroupCount[];
};

type UseAllTasksParams = {
  search?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
  perPage?: number;
};

export const useTaskSearch = (
  params: UseAllTasksParams & {
    projectIds?: Project['id'][];
    status?: string;
    groupBy?: ServerGroupBy;
  } = {}
) => {
  const { axiosInstance } = useApi();

  return useInfiniteQuery<TasksResponse>({
    queryKey: queryKeys.tasks.infinite(params).queryKey,
    initialPageParam: 1,
    queryFn: async ({ pageParam }: { pageParam?: unknown }) => {
      const rawPage = pageParam ?? 1;
      const parsed = Number(rawPage);
      const page = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;

      // Build params object for Axios
      const queryParams: Record<string, unknown> = {
        page,
        per_page: params.perPage,
      };

      // Ransack search
      if (params.search) queryParams['q[name_cont]'] = params.search;

      // Ransack sort
      if (params.sort) queryParams['q[s]'] = `${params.sort} ${params.direction || 'asc'}`;

      // Ransack project filter (multiple projects)
      if (params.projectIds && params.projectIds.length > 0) {
        queryParams['q[project_id_in]'] = params.projectIds;
      }

      // Status filter
      if (params.status) {
        queryParams['status'] = params.status;
      }

      if (params.groupBy) {
        queryParams['group_by'] = params.groupBy;
      }

      const { data } = await axiosInstance.get<TasksResponse>(`/api/v1/tasks/search`, {
        params: queryParams,
      });
      return data;
    },
    getNextPageParam: (lastPage: TasksResponse) => lastPage.pagy.next ?? undefined,
    getPreviousPageParam: (firstPage: TasksResponse) => firstPage.pagy.prev ?? undefined,
  });
};

// Fetch a single project task
export const useTask = (taskId: string | number) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.tasks.detail(taskId).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<Task>(`/api/v1/tasks/${taskId}`);
      return data;
    },
  });
};

export const OPTIMISTIC_TASK_ID_PREFIX = '__optimistic__';

export const isOptimisticTaskId = (id: Task['id']): boolean =>
  typeof id === 'string' && id.startsWith(OPTIMISTIC_TASK_ID_PREFIX);

let optimisticTaskCounter = 0;

const buildOptimisticTask = (newTask: Partial<Task>): Task => {
  optimisticTaskCounter += 1;
  const now = new Date().toISOString();
  return {
    id: `${OPTIMISTIC_TASK_ID_PREFIX}${optimisticTaskCounter}`,
    name: newTask.name ?? '',
    project_milestone_id: newTask.project_milestone_id ?? null,
    task_category_id: newTask.task_category_id ?? null,
    location_id: newTask.location_id ?? null,
    trade_id: newTask.trade_id ?? null,
    description: newTask.description ?? '',
    estimated_cost: newTask.estimated_cost ?? null,
    status: 'scheduled',
    archived_at: null,
    project_id: newTask.project_id,
    company_id: 0 as unknown as Task['company_id'],
    created_at: now,
    updated_at: now,
    merged_intervals: [],
    end_date: null,
    start_date: null,
    date_lock_mode: 'locked',
    planned_start_date: null,
    planned_duration_days: null,
    latest_cost_price: null,
    latest_sales_price: null,
    completion_percentage: 0,
    slack_to_next_days: null,
    slack_to_finish_days: null,
    is_critical: null,
  };
};

type InfiniteTasksCache = { pages: TasksResponse[]; pageParams: unknown[] } | undefined;

// Create a new project task
export const useCreateTask = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newTask: Partial<Task>) => {
      return axiosInstance.post<Task>(`/api/v1/tasks`, newTask);
    },
    onMutate: async (newTask) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks.infinite._def });
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks.all.queryKey });

      const optimisticTask = buildOptimisticTask(newTask);

      const previousInfinite = queryClient.getQueriesData<InfiniteTasksCache>({
        queryKey: queryKeys.tasks.infinite._def,
      });
      const previousAll = queryClient.getQueryData<Task[]>(queryKeys.tasks.all.queryKey);

      queryClient.setQueriesData<InfiniteTasksCache>(
        { queryKey: queryKeys.tasks.infinite._def },
        (data) => {
          if (!data?.pages?.length) return data;
          const [firstPage, ...restPages] = data.pages;
          const nextFirstPage: TasksResponse = {
            ...firstPage,
            tasks: [optimisticTask, ...firstPage.tasks],
            pagy: { ...firstPage.pagy, count: firstPage.pagy.count + 1 },
          };
          return { ...data, pages: [nextFirstPage, ...restPages] };
        }
      );

      if (previousAll) {
        queryClient.setQueryData<Task[]>(queryKeys.tasks.all.queryKey, [
          optimisticTask,
          ...previousAll,
        ]);
      }

      return { previousInfinite, previousAll };
    },
    onSuccess: () => {
      track('task_created');
    },
    onError: (_err, _newTask, context) => {
      if (context?.previousInfinite) {
        for (const [key, data] of context.previousInfinite) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousAll !== undefined) {
        queryClient.setQueryData(queryKeys.tasks.all.queryKey, context.previousAll);
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all.queryKey });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.infinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.taskCompletion._def,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.planningProjects._def,
      });
      if (variables.project_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.economyReport(variables.project_id).queryKey,
        });
      }
    },
  });
};

// Update an existing project task
export const useUpdateTask = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      updatedTask,
    }: {
      taskId: Task['id'];
      updatedTask: Partial<Task>;
    }) => {
      const { data } = await axiosInstance.put<Task>(`/api/v1/tasks/${taskId}`, {
        task: updatedTask,
      });
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.byProject(data.project_id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.detail(data.id).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all.queryKey });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.infinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.planningProjects._def,
      });
      if (data.project_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.economyReport(data.project_id).queryKey,
        });
      }
    },
  });
};

// Bulk-update selected tasks
export const useBulkUpdateTasks = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskIds,
      updates,
    }: {
      taskIds: Task['id'][];
      updates: Partial<
        Pick<
          Task,
          | 'project_milestone_id'
          | 'task_category_id'
          | 'location_id'
          | 'trade_id'
          | 'date_lock_mode'
        >
      >;
    }) => {
      const { data } = await axiosInstance.patch<Task[]>(`/api/v1/tasks/bulk_update`, {
        task_ids: taskIds,
        updates,
      });
      return data;
    },
    onSuccess: (tasks) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all.queryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.infinite._def });
      queryClient.invalidateQueries({ queryKey: queryKeys.planningProjects._def });
      const projectIds = new Set(tasks.map((t) => t.project_id).filter(Boolean) as Project['id'][]);
      projectIds.forEach((projectId) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.byProject(projectId).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.economyReport(projectId).queryKey,
        });
      });
      tasks.forEach((task) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.detail(task.id).queryKey,
        });
      });
    },
  });
};

// Delete a project task
export const useDeleteTask = (task: Task) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await axiosInstance.delete(`/api/v1/tasks/${task.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all.queryKey });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.detail(task.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.infinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.resources.list({ projectId: task.project_id }).queryKey,
      });
    },
  });
};

// Delete many tasks in parallel (fan-out — no backend bulk endpoint)
export const useBulkDeleteTasks = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tasks: Pick<Task, 'id' | 'project_id'>[]) => {
      await Promise.all(tasks.map((task) => axiosInstance.delete(`/api/v1/tasks/${task.id}`)));
      return tasks;
    },
    onSuccess: (tasks) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all.queryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.infinite._def });
      tasks.forEach((task) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(task.id).queryKey });
      });
      const projectIds = new Set(tasks.map((t) => t.project_id).filter(Boolean) as Project['id'][]);
      projectIds.forEach((projectId) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.byProject(projectId).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.resources.list({ projectId }).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.economyReport(projectId).queryKey,
        });
      });
    },
  });
};

// Mark many tasks as complete in parallel (fan-out — no backend bulk endpoint)
export const useBulkCompleteTasks = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tasks: Pick<Task, 'id' | 'project_id'>[]) => {
      await Promise.all(
        tasks.map((task) =>
          axiosInstance.post<Task>(`/api/v1/tasks/${task.id}/completion_states`, {
            task_completion_state: { percentage: 100 },
          })
        )
      );
      return tasks;
    },
    onSuccess: (tasks) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all.queryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.infinite._def });
      tasks.forEach((task) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(task.id).queryKey });
      });
      const projectIds = new Set(tasks.map((t) => t.project_id).filter(Boolean) as Project['id'][]);
      projectIds.forEach((projectId) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.byProject(projectId).queryKey,
        });
      });
    },
  });
};

// Fetch economy report for a task
export const useTaskEconomyReport = (taskId: Task['id']) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.tasks.economyReport(taskId).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<TaskEconomyReport>(
        `/api/v1/tasks/${taskId}/economy_report`
      );
      return data;
    },
  });
};

// Create an expense on a task
export const useCreateTaskExpense = (taskId: Task['id']) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (expense: { cost: number; comment: string; resource_id?: number | null }) => {
      const { data } = await axiosInstance.post<TaskExpense>(`/api/v1/tasks/${taskId}/expenses`, {
        task_expense: expense,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.economyReport(taskId).queryKey,
      });
    },
  });
};

// Delete an expense from a task
export const useDeleteTaskExpense = (taskId: Task['id']) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (expenseId: string | number) => {
      await axiosInstance.delete(`/api/v1/tasks/${taskId}/expenses/${expenseId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.economyReport(taskId).queryKey,
      });
    },
  });
};

// Fetch cost price history for a task (newest first)
export const useTaskCostPrices = (taskId: Task['id']) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.tasks.costPrices(taskId).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<TaskCostPrice[]>(
        `/api/v1/tasks/${taskId}/cost_prices`
      );
      return data;
    },
    enabled: !!taskId,
  });
};

// Create a new cost price for a task
export const useCreateTaskCostPrice = (
  taskId: Task['id'],
  projectId: Project['id'] | undefined
) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (estimate: { cost: number; comment?: string }) => {
      const { data } = await axiosInstance.post<TaskCostPrice>(
        `/api/v1/tasks/${taskId}/cost_prices`,
        { task_cost_price: estimate }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.costPrices(taskId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.economyReport(taskId).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all.queryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.infinite._def });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(taskId).queryKey });
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.byProject(projectId).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.economyReport(projectId).queryKey,
        });
      }
    },
  });
};

// Fetch sales price history for a task (newest first)
export const useTaskSalesPrices = (taskId: Task['id']) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.tasks.salesPrices(taskId).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<TaskSalesPrice[]>(
        `/api/v1/tasks/${taskId}/sales_prices`
      );
      return data;
    },
    enabled: !!taskId,
  });
};

// Create a new sales price for a task
export const useCreateTaskSalesPrice = (
  taskId: Task['id'],
  projectId: Project['id'] | undefined
) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (estimate: { price: number; comment?: string }) => {
      const { data } = await axiosInstance.post<TaskSalesPrice>(
        `/api/v1/tasks/${taskId}/sales_prices`,
        { task_sales_price: estimate }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.salesPrices(taskId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.economyReport(taskId).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all.queryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.infinite._def });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(taskId).queryKey });
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.byProject(projectId).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.economyReport(projectId).queryKey,
        });
      }
    },
  });
};

// Fetch hour estimate history for a task (newest first)
export const useTaskHourEstimates = (taskId: Task['id']) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.tasks.hourEstimates(taskId).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<TaskHourEstimate[]>(
        `/api/v1/tasks/${taskId}/hour_estimates`
      );
      return data;
    },
    enabled: !!taskId,
  });
};

export const useArchiveTask = (taskId: Task['id']) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.patch<Task>(`/api/v1/tasks/${taskId}/archive`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all.queryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(taskId).queryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.infinite._def });
    },
  });
};

export const useUnarchiveTask = (taskId: Task['id']) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.patch<Task>(`/api/v1/tasks/${taskId}/unarchive`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all.queryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(taskId).queryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.infinite._def });
    },
  });
};

// Create a new hour estimate for a task
export const useCreateTaskHourEstimate = (taskId: Task['id']) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (estimate: { hours: number; comment?: string }) => {
      const { data } = await axiosInstance.post<TaskHourEstimate>(
        `/api/v1/tasks/${taskId}/hour_estimates`,
        { task_hour_estimate: estimate }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.hourEstimates(taskId).queryKey,
      });
    },
  });
};

// Create cost price/hour estimates for a newly created task, before its id is known to any hook
export const useCreateTaskEstimates = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  const createEstimates = async (
    taskId: Task['id'],
    projectId: Project['id'] | null,
    { cost, price, hours }: { cost: number | null; price: number | null; hours: number | null }
  ) => {
    if (cost !== null) {
      await axiosInstance.post(`/api/v1/tasks/${taskId}/cost_prices`, {
        task_cost_price: { cost },
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.costPrices(taskId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.economyReport(taskId).queryKey,
      });
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.economyReport(projectId).queryKey,
        });
      }
    }

    if (price !== null) {
      await axiosInstance.post(`/api/v1/tasks/${taskId}/sales_prices`, {
        task_sales_price: { price },
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.salesPrices(taskId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.economyReport(taskId).queryKey,
      });
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.economyReport(projectId).queryKey,
        });
      }
    }

    if (hours !== null) {
      await axiosInstance.post(`/api/v1/tasks/${taskId}/hour_estimates`, {
        task_hour_estimate: { hours },
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.hourEstimates(taskId).queryKey,
      });
    }
  };

  return { createEstimates };
};

export interface TaskIntervalCompletionState {
  id: number;
  interval_id: number;
  interval_start_date: string;
  interval_end_date: string;
  resource_name: string | null;
  resource_color: string | null;
  percentage: number;
  status: 'pending' | 'approved' | 'declined';
  created_by_name: string;
  created_at: string;
}

export const useTaskIntervalCompletionStates = (taskId: Task['id']) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.tasks.intervalCompletionStates(taskId).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<TaskIntervalCompletionState[]>(
        `/api/v1/tasks/${taskId}/interval_completion_states`
      );
      return data;
    },
    enabled: !!taskId,
  });
};
